// Single source of truth for gating unreleased features.
//
// VIDEO_RECORDER_ENABLED — controls the entire gift-recorder + gift-message
// feature set. Until product / legal approval to ship, this MUST stay `false`.
// Flipping it to `true` is the only change required to expose the feature
// across the whole app.
//
// When `false`:
//   • Sender self-record prompt + slot-based recorder (gift-flow step 1) — hidden
//   • Navigation never lands on step1SubStep 3 (prompt) or 4 (recorder)
//   • Contributor "Tell Your Story & Send" tile (gift-flow step 3 upload) — hidden
//   • Contributor occasion picker + recorder modal — hidden
//   • Receiver-side "Tell Your Story" Yes/No prompt screen (gift-content) — skipped
//   • Receiver-side recorder + occasion picker (gift-content) — hidden
//   • GiftMessagePlayer popup (the "play your gift" overlay on gift-content) — hidden
//   • Per-folder "🎁 Open Gift" pills on gift-content folder cards — hidden
//   • Phone-as-webcam (/mobile-camera/:sessionId) — renders an unavailable view
//
// When `true`: everything above is restored to its normal behaviour.
//
// Do NOT add additional conditions for hiding the feature in other places
// without also gating them on this flag — the goal is one switch to rule
// them all so re-enabling is a one-line change.
export const VIDEO_RECORDER_ENABLED = true;
export const VIDEO_RECORDER_COMMENTARY_ENABLED = false;

// GUEST_INVITE_ENABLED — gates the live "Add guest" / meeting feature inside
// the gift recorder, where the host invites a remote person to join the
// recording session over WebRTC (shareable /interview/join link, PIP/side-by-
// side guest tile, active-speaker ring).
//
// When `false`:
//   • The "Add guest" button + connected-guest badge on the recorder stage — hidden
//   • The guest invite-link popup — hidden
//   • The remote-guest PIP overlay — hidden
// The host can still record solo exactly as before; nothing else changes.
//
// This does NOT control the standalone /interview/* pages (intro/host/review),
// which are a separate entry point.
export const GUEST_INVITE_ENABLED = true;

// SHARED_FEATURE_ENABLED — gates the "shared folders" feature end-to-end, i.e.
// the flow where a user creates a shareable link so others can contribute media
// into a shared folder. This flag ONLY controls the entry points into the
// feature; flip it to expose or hide them everywhere in one place.
//
// When `false`:
//   • The "Shared Folders" item in the upload menu (UploadDropdown) — hidden
//   • The "Shared" tab on the /your-videos page — hidden (and if a stale
//     sessionStorage value had it selected, the page falls back to Videos)
//
// When `true`: both entry points are restored to their normal behaviour.
//
// This value is now driven by the backend: getMyProfile returns
// `features.share_folders`, and app.jsx calls setSharedFeatureEnabled() on load
// to flip this. It is an ES-module live binding, so every `import
// { SHARED_FEATURE_ENABLED }` reads the current value on each render — no
// consumer changes are needed. The initial value is seeded from a localStorage
// cache so the correct state is available on reload before the profile call
// resolves (avoids UI flicker). The mutable export is intentional — it is the
// ES-module live binding that lets consumers pick up the backend value without
// any changes, so the lint rule is disabled here on purpose.
// eslint-disable-next-line import/no-mutable-exports
export let SHARED_FEATURE_ENABLED = (() => {
  try {
    return localStorage.getItem('feature_share_folders') === '1';
  } catch (e) {
    return false;
  }
})();

// Called once from app.jsx after getMyProfile resolves. Accepts bool / '1' / 1.
export const setSharedFeatureEnabled = (value) => {
  SHARED_FEATURE_ENABLED = value === true || value === '1' || value === 1;
  try {
    localStorage.setItem('feature_share_folders', SHARED_FEATURE_ENABLED ? '1' : '0');
  } catch (e) {
    // localStorage unavailable — in-memory value still applies for this session
  }
};

// GIFT_PLAN_CARDS_MOBILE_VERTICAL — controls how the gift plan cards are laid
// out on mobile (≤900px) in the payment step.
//   • true  → cards stack vertically, one below the other (no swipe carousel)
//   • false → original horizontal swipe carousel with dots
// Desktop is unaffected either way. Flip this one line to switch layouts.
export const GIFT_PLAN_CARDS_MOBILE_VERTICAL = true;
